import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router";
import Button from "@components/common/Button";
import withTargetCloseAnimation from "./withTargetCloseAnimation";

const ANIMATION_DURATION_MS = 500;
const AnimatedDialog: React.FC<
	{
		ref: React.Ref<HTMLDialogElement>;
		isAnimatingIn: boolean;
		animateCloseToTarget: boolean;
		closeAnimationTargetRef: React.RefObject<HTMLElement> | null;
		closeAnimationFallbackTransform: string;
		open?: boolean;
		onClose?: (e: React.ChangeEvent<HTMLDialogElement>) => void;
	} & React.HTMLAttributes<HTMLDialogElement>
> = withTargetCloseAnimation("dialog") as any;

type ModalProps = {
	main?: React.ReactNode;
	header?: React.ReactNode;
	footer?: React.ReactNode;
	check?: () => boolean;
	closeElement?: React.ReactNode;
	closeVariant?: "primary" | "secondary" | "danger";
	onLoad?: (formElement: HTMLFormElement) => void;
	onChange?: (e: React.FormEvent<HTMLFormElement>) => void;
	onClose?: (dialogElement: HTMLDialogElement) => void;
	onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
	isOpen?: boolean;
	animateCloseToTarget?: boolean;
	closeAnimationTargetRef?: React.RefObject<HTMLElement>;
	padding?: string;
	mainPadding?: string;
	minW?: string;
	maxW?: string;
	dialogClass?: string;
	formClass?: string;
	headerClass?: string;
	mainClass?: string;
	footerClass?: string;
};

const Modal = ({
	main = null,
	header = null,
	footer = null,
	check = null,
	closeElement = null,
	closeVariant = "secondary",
	onLoad = undefined,
	onChange = undefined,
	onClose = () => {},
	onSubmit = () => {},
	isOpen = false,
	animateCloseToTarget = false,
	closeAnimationTargetRef = null,
	padding = "p-6",
	mainPadding = null,
	minW = "min-w-[300px]",
	maxW = "max-w-[800px]",
	dialogClass = "",
	formClass = "",
	headerClass = "",
	mainClass = "overflow-y-scroll",
	footerClass = "",
}: ModalProps) => {
	const location = useLocation();
	const prevLocationRef = useRef(null);
	const modalRef = useRef(null);
	const dialogRef = useRef(null);
	const formRef = useRef(null);
	const closeTimeoutRef = useRef(null);
	const hasTriggeredLoadRef = useRef(false);
	const [shouldRender, setShouldRender] = useState(isOpen);
	const [isAnimatingIn, setIsAnimatingIn] = useState(false);

	useEffect(() => {
		if (closeTimeoutRef.current) {
			window.clearTimeout(closeTimeoutRef.current);
			closeTimeoutRef.current = null;
		}

		if (isOpen) {
			setShouldRender(true);
			return;
		}

		setIsAnimatingIn(false);
		closeTimeoutRef.current = window.setTimeout(() => {
			setShouldRender(false);
		}, ANIMATION_DURATION_MS);

		return () => {
			if (closeTimeoutRef.current) {
				window.clearTimeout(closeTimeoutRef.current);
				closeTimeoutRef.current = null;
			}
		};
	}, [isOpen]);

	useEffect(() => {
		if (!shouldRender) return;

		const frame = window.requestAnimationFrame(() => {
			setIsAnimatingIn(isOpen);
		});

		return () => {
			window.cancelAnimationFrame(frame);
		};
	}, [shouldRender, isOpen]);

	useEffect(
		() => () => {
			if (closeTimeoutRef.current) {
				window.clearTimeout(closeTimeoutRef.current);
				closeTimeoutRef.current = null;
			}
		},
		[]
	);

	useEffect(() => {
		const handleEscKey = (e) => {
			if (e.key === "Escape" && isOpen) {
				if (typeof check === "function" && !check()) return false;
				onClose(dialogRef.current);
				return true;
			}
		};

		if (isOpen) {
			document.addEventListener("keydown", handleEscKey);
		}

		return () => {
			document.removeEventListener("keydown", handleEscKey);
		};
	}, [isOpen, check, onClose]);

	useEffect(() => {
		if (!isOpen) {
			prevLocationRef.current = null;
			return;
		}
		const nextLocationValue = `${location.pathname}${location.search}${location.hash}${location.key || ""}`;
		if (prevLocationRef.current === null) {
			prevLocationRef.current = nextLocationValue;
			return;
		}

		if (prevLocationRef.current !== nextLocationValue) {
			if (typeof check === "function" && !check()) return;
			onClose(dialogRef.current);
		}

		prevLocationRef.current = nextLocationValue;
	}, [isOpen, check, onClose, location]);

	useEffect(() => {
		if (!isOpen) {
			hasTriggeredLoadRef.current = false;
			return;
		}

		if (hasTriggeredLoadRef.current || typeof onLoad !== "function") return;
		if (!(formRef.current instanceof HTMLFormElement)) return;

		onLoad(formRef.current);
		hasTriggeredLoadRef.current = true;
	}, [isOpen, onLoad, shouldRender]);

	const handleOutsideClick = (e) => {
		if (!isOpen) return false;

		if (e.target === modalRef.current) {
			e.stopPropagation();
			onClose(dialogRef.current);
			return true;
		}
		return false;
	};

	const renderContent = (content) => {
		if (!content) return null;

		if (React.isValidElement(content) || typeof content === "string") {
			return content;
		} else {
			console.warn("Modal: unsupported content type:", typeof content);
			return null;
		}
	};

	if (!shouldRender) return null;

	return createPortal(
		<div
			ref={modalRef}
			className={`fixed inset-0 flex items-center justify-center bg-black/50 z-[150] transition-opacity duration-500 ease-out ${
				isAnimatingIn ? "opacity-100" : "opacity-0 pointer-events-none"
			}`}
			onClick={handleOutsideClick}
		>
			<AnimatedDialog
				ref={dialogRef}
				isAnimatingIn={isAnimatingIn}
				animateCloseToTarget={animateCloseToTarget}
				closeAnimationTargetRef={closeAnimationTargetRef}
				closeAnimationFallbackTransform="translateY(8px) scale(0.95)"
				className={`relative bg-white rounded-md shadow-xl max-w-[90%] max-h-[90%] overflow-auto p-0 border-0 transition-all duration-500 ease-out ${
					isAnimatingIn ? "opacity-100" : "opacity-0"
				} ${dialogClass || ""}`}
				open={true}
				onClose={(e) => {
					onClose(e.target);
				}}
			>
				<form
					ref={formRef}
					onSubmit={onSubmit}
					onChange={onChange}
					method="dialog"
					className={`flex flex-col ${minW} ${maxW} max-h-[80vh] overflow-hidden ${
						formClass || ""
					}`}
				>
					{header && (
						<header
							className={`${padding} z-50 flex items-center justify-between bg-white shadow-md border-b border-grey ${
								headerClass || ""
							}`}
							style={{ display: header ? "" : "none" }}
						>
							{renderContent(header)}
							<Button
								variant={closeVariant}
								outline
								onClick={onClose}
							>
								{closeElement || "Close"}
							</Button>
						</header>
					)}

					{main && (
						<main
							className={`flex-1 relative ${
								mainPadding || padding
							} ${mainClass || ""}`}
							style={{ display: main ? "" : "none" }}
						>
							{renderContent(main)}
						</main>
					)}

					{footer && (
						<footer
							className={`${padding} bg-white bottom-0 border-t border-grey flex justify-end ${
								footerClass || ""
							}`}
							style={{ display: footer ? "" : "none" }}
						>
							{renderContent(footer)}
						</footer>
					)}
				</form>
			</AnimatedDialog>
		</div>,
		document.body
	);
};

export default Modal;
